import {
	Bot,
	Clock,
	Eye,
	FileEdit,
	GitMerge,
	ShieldAlert,
	User,
} from "lucide-react";
import { isStale } from "./utils";

const pipelineStatus = (mr) => mr.headPipeline?.status?.toLowerCase();

export const isReadyToMerge = (mr) => {
	const status = pipelineStatus(mr);
	const passed = status === "success" || status === "passed";
	return (
		mr.state === "opened" &&
		passed &&
		!mr.conflicts &&
		mr.approvalsLeft === 0 &&
		!mr.draft
	);
};

export const needsMyReview = (mr) =>
	mr.state === "opened" &&
	!mr.author?.isMe &&
	["UNREVIEWED", "UNAPPROVED", "REVIEW_STARTED"].includes(mr.myReview);

/**
 * Quick-filter definitions shared by the chip bar and the filtering logic.
 *
 * Each filter is only applied when active. A filter either:
 *  - `keep(mr)`    -> a positive filter; keep MRs that match
 *  - `exclude(mr)` -> an exclusion filter; drop MRs that match
 */
export const QUICK_FILTERS = [
	{
		id: "needsReview",
		label: "Needs my review",
		tooltipSubject: "MRs needing my review",
		icon: Eye,
		keep: needsMyReview,
	},
	{
		id: "readyToMerge",
		label: "Ready to merge",
		tooltipSubject: "Ready-to-merge MRs",
		icon: GitMerge,
		keep: isReadyToMerge,
	},
	{
		id: "myMRsOnly",
		label: "Show my MRs only",
		tooltipSubject: "My MRs",
		icon: User,
		keep: (mr) => !!mr.author?.isMe,
	},
	{
		id: "hideDrafts",
		label: "Hide drafts",
		tooltipSubject: "Draft MRs",
		icon: FileEdit,
		exclude: (mr) => !!mr.draft,
	},
	{
		id: "hideStale",
		label: "Hide stale (14d+)",
		tooltipSubject: "Stale MRs (14d+)",
		icon: Clock,
		exclude: (mr) => isStale(mr),
	},
	{
		id: "hideConflicts",
		label: "Hide conflicts",
		tooltipSubject: "Conflicting MRs",
		icon: ShieldAlert,
		exclude: (mr) => !!mr.conflicts,
	},
	{
		id: "hideBots",
		label: "Hide bots",
		tooltipSubject: "Bot-authored MRs",
		icon: Bot,
		exclude: (mr) => mr.author?.bot ?? false,
	},
];

// Applies all active quick filters to a list of MRs.
export const applyQuickFilters = (mrs, active) => {
	if (!active) return mrs;
	const enabled = QUICK_FILTERS.filter((f) => active[f.id]);
	if (enabled.length === 0) return mrs;

	return mrs.filter((mr) =>
		enabled.every((f) => {
			if (f.keep) return f.keep(mr);
			if (f.exclude) return !f.exclude(mr);
			return true;
		}),
	);
};
